import { Injectable, InjectionToken } from '@angular/core';

/**
 * Options to customize loading behavior
 */
/**
 * Interface defining options for the loading service.
 * @interface LoadingOptions
 */
export interface LoadingOptions {
  /** Title to display in the loading dialog */
  title?: string;
  /** Message to display during loading */
  message?: string;
  /** Indicates whether the operation can be canceled */
  cancelable?: boolean;
  /** Callback function executed when canceling */
  onCancel?: () => void;
  /** Delay time before showing the loading (in ms) */
  delay?: number;
  /** Maximum loading time before auto-closing (in ms) */
  timeout?: number;
  /** Additional implementation-specific data */
  [key: string]: unknown;
}

/**
 * Reference to an active loading instance
 */
export interface LoadingRef {
  /** Closes the loading */
  close(): void;
  /** Updates the loading message */
  updateMessage(message: string): void;
  /** Indicates if the loading is active */
  isActive(): boolean;
}

@Injectable()
export abstract class LoadingService {
  /**
   * Shows a loading dialog
   * @param options Configuration options
   * @returns Reference to the active loading
   */
  abstract show(options?: LoadingOptions): LoadingRef;

  /**
   * Closes all active loading dialogs
   */
  abstract hideAll(): void;

  /**
   * Indicates if there is any active loading
   */
  abstract hasActiveLoading(): boolean;
}

/**
 * Injection token for the loading service
 */
export const LOADING_SERVICE = new InjectionToken<LoadingService>(
  'LOADING_SERVICE',
);
